import { inject, Injectable } from '@angular/core';
import {
  CaseDownloadDocument,
  ExternalLinkDownloadDocument,
  ReferenceAssociationDownloadDocument,
  ReferenceDownloadDocument,
  TrademarkImageDownloadDocument,
} from '@model/download-action.model';
import {
  ExcelDownloadRequest,
  ExcelExportOptions,
  ExcelResult,
  ExternalLinkDownloadRequest,
  IPCaseDownloadRequest,
  ReferenceAssociationDownloadRequest,
  ReferenceDownloadRequest,
  TrademarkImageDownloadRequest,
} from '@model/excel-download-request.model';
import { CaseReferenceDetails, CommonIP, CommonIPTrifold, IP, ReferenceDetails, TrademarkDetails } from '@model/ftf-api.model';
import { ApiService } from '@service/api.service';
import { LoggerService } from '@service/logger.service';
import { getFileNameFromPath } from '@utils/file-utils';
import { EMPTY, forkJoin, from, Observable, of } from 'rxjs';
import { catchError, filter, map, mergeMap, takeUntil } from 'rxjs/operators';

import { ExcelWriterService } from './excel-writer.service';
import { SettingsService } from './settings.service';

interface ReferenceAssociation extends CaseReferenceDetails {
  identifier: string;
}

@Injectable({
  providedIn: 'root',
})
export class ExcelDownloadService {
  private logger = inject(LoggerService);
  private apiService = inject(ApiService);
  private settingsService = inject(SettingsService);
  private excelWriterService = inject(ExcelWriterService);

  /**
   * Main entry point: routes request to appropriate handler based on template type.
   * Called from ExcelService.executePrepareActionRequest().
   */
  public handleRequest(request: ExcelDownloadRequest, source$: Observable<string>, options?: ExcelExportOptions): Observable<ExcelResult> {
    switch (request.templateType) {
      case 'ReferenceDocument':
        return this.handleReferenceDocumentRequest(request, source$, options);
      case 'ReferenceAssociation':
        return this.handleReferenceAssociationRequest(request, source$, options);
      case 'IPCaseDocuments':
        return this.handleIPCaseDocumentsRequest(request, source$, options);
      case 'ExternalLink':
        return this.handleExternalLinkRequest(request, source$, options);
      case 'TrademarkImage':
        return this.handleTrademarkImageRequest(request, source$, options);
      default:
        this.logger.warn('ExcelDownloadService.handleRequest: unhandled template type');
        return EMPTY;
    }
  }
  /**
   * Handles ReferenceAssociation template type.
   * Streams case identifiers, fetches their patent references via API,
   * and transforms each reference into a ReferenceAssociationDownloadDocument.
   *
   * Uses mergeMap for concurrent API calls as identifiers arrive (streaming).
   * This allows Excel to be populated incrementally rather than waiting for all data.
   */
  private handleReferenceAssociationRequest(
    request: ReferenceAssociationDownloadRequest,
    source$: Observable<string>,
    options?: ExcelExportOptions
  ): Observable<ExcelResult> {
    const sourceTransform$ = source$.pipe(
      mergeMap((identifier) =>
        this.apiService.findPatentReferences(identifier).pipe(
          mergeMap((references) => from(references)),
          map((reference) => ({ ...reference, identifier }) as ReferenceAssociation),
          catchError((err) => {
            this.logger.warn(`ExcelDownloadService: failed to resolve references for case "${identifier}"`, err);
            return EMPTY;
          })
        )
      ),
      filter((item): item is ReferenceAssociation => item !== null),
      map((item) => this.transformReferenceAssociationToDocument(item)),
      takeUntil(options?.cancel$ ?? EMPTY)
    );

    return this.excelWriterService.writeReferenceAssociation(sourceTransform$, options);
  }

  /**
   * Handles IPCaseDocuments template type.
   * Streams case identifiers, fetches their full IP data with trifold documents,
   * flattens the documents across requested trifold sections, and transforms to CaseDownloadDocument.
   */
  private handleIPCaseDocumentsRequest(
    ipRequest: IPCaseDownloadRequest,
    source$: Observable<string>,
    options?: ExcelExportOptions
  ): Observable<ExcelResult> {
    const sourceTransform$ = source$.pipe(
      mergeMap((identifier) => {
        const { ipType } = ipRequest;
        return this.apiService.findIPTrifold(identifier, ipType).pipe(
          catchError((err) => {
            this.logger.warn(`ExcelDownloadService: failed to resolve case "${identifier}"`, err);
            return EMPTY;
          })
        );
      }),
      mergeMap((trifold) => {
        const docs = this.flattenCaseTrifoldDocumentsFromTrifold(trifold, ipRequest.trifolds!);
        this.logger.info(`[flattenTrifold] Case ${trifold.identifier}: got ${docs.length} documents`);
        return ipRequest.trifolds && ipRequest.trifolds.length > 0 ? from(docs) : EMPTY;
      }),
      takeUntil(options?.cancel$ ?? EMPTY)
    );

    return this.excelWriterService.writeCaseDocument(sourceTransform$, options);
  }

  /**
   * Handles ReferenceDocument template type.
   * Streams reference names, fetches their details via API,
   * filters by reference type, and transforms to ReferenceDownloadDocument.
   */
  private handleReferenceDocumentRequest(
    refRequest: ReferenceDownloadRequest,
    source$: Observable<string>,
    options?: ExcelExportOptions
  ): Observable<ExcelResult> {
    const sourceTransform$ = source$.pipe(
      mergeMap((refName) =>
        this.apiService.findReferenceDetailsByName(refName).pipe(
          catchError((err) => {
            this.logger.warn(`ExcelDownloadService: failed to resolve reference "${refName}"`, err);
            return EMPTY;
          })
        )
      ),
      filter((item): item is ReferenceDetails => item !== null),
      filter((details) => this.matchesReferenceType(details, refRequest.referenceTypes)),
      map((item) => this.transformReferenceDetailsToDocument(item)),
      takeUntil(options?.cancel$ ?? EMPTY)
    );
    return this.excelWriterService.writeReferenceDocument(sourceTransform$, options);
  }

  /**
   * Handles ExternalLink template type.
   */
  private handleExternalLinkRequest(
    extLinkRequest: ExternalLinkDownloadRequest,
    source$: Observable<string>,
    options?: ExcelExportOptions
  ): Observable<ExcelResult> {
    const sourceTransform$ = source$.pipe(
      mergeMap((caseNumber) =>
        this.apiService.findIP(caseNumber, extLinkRequest.ipType).pipe(
          catchError((err) => {
            this.logger.warn(`ExcelDownloadService: failed to resolve case "${caseNumber}" for external link`, err);
            return EMPTY;
          })
        )
      ),
      filter((item): item is CommonIP => item !== null),
      map((ip) => {
        return this.transformExternalLinkToDocument(ip);
      }),
      takeUntil(options?.cancel$ ?? EMPTY)
    );
    return this.excelWriterService.writeExternalLinkDocument(sourceTransform$, options);
  }

  /**
   * Handles TrademarkImage template type.
   */
  private handleTrademarkImageRequest(
    request: TrademarkImageDownloadRequest,
    source$: Observable<string>,
    options?: ExcelExportOptions
  ): Observable<ExcelResult> {
    const sourceTransform$ = source$.pipe(
      mergeMap((identifier) =>
        this.apiService.findTrademarkDetails(identifier, true).pipe(
          catchError((err) => {
            this.logger.warn(`ExcelDownloadService: failed to resolve trademark "${identifier}" for image`, err);
            return EMPTY;
          })
        )
      ),
      filter((item): item is TrademarkDetails => item !== null),
      map((details) => this.transformTrademarkImageDetailsToDocument(details)),
      takeUntil(options?.cancel$ ?? EMPTY)
    );
    return this.excelWriterService.writeTrademarkImageDocument(sourceTransform$, options);
  }

  private transformExternalLinkToDocument(ip: CommonIP): ExternalLinkDownloadDocument {
    const doc = new ExternalLinkDownloadDocument();
    doc.case = ip.identifier;
    doc.link = ip.caseUrl;
    doc.environment = this.getEnvironmentFromBaseUrl();
    return doc;
  }

  private transformTrademarkImageDetailsToDocument(details: TrademarkDetails): TrademarkImageDownloadDocument {
    const doc = new TrademarkImageDownloadDocument();
    doc.case = details.trademarkNm;
    doc.documentName = getFileNameFromPath(details.depictionMark ?? '');
    doc.environment = this.getEnvironmentFromBaseUrl();
    return doc;
  }

  /**
   * Transform helper: ReferenceDetails → ReferenceDownloadDocument
   */
  private transformReferenceDetailsToDocument(details: ReferenceDetails): ReferenceDownloadDocument {
    const doc = new ReferenceDownloadDocument();
    doc.referenceName = details.titleDisplay;
    doc.documentName = details.fileNameDisplay ?? details.fileName;
    doc.type = details.referenceTypeDisplay;
    doc.patentNumber = details.patentNumber;
    doc.pubNumber = details.publicationNumber;
    doc.issueDate = details.issueDate ? new Date(details.issueDate).toISOString().split('T')[0] : undefined;
    doc.applicant = details.applicant;
    doc.inventor = details.inventors;
    doc.assignee = details.assignee;
    doc.countryCode = details.countryCode;
    doc.kindCode = details.kindCode;
    doc.nameOfAuthor = details.author;
    doc.titleOfArticle = details.articleTitle;
    doc.titleOfItem = details.itemTitle;
    doc.publisher = details.publisher;
    doc.pages = details.pages;
    doc.city = details.city;
    doc.volume = details.volumeNumber;
    doc.nplDate = details.articleDate;
    doc.customCitation = details.customCitation;
    doc.environment = this.getEnvironmentFromBaseUrl();
    return doc;
  }

  /**
   * Transform helper: ReferenceAssociation → ReferenceAssociationDownloadDocument
   */
  private transformReferenceAssociationToDocument(item: ReferenceAssociation): ReferenceAssociationDownloadDocument {
    const doc = new ReferenceAssociationDownloadDocument();
    doc.case = item.identifier;
    doc.referenceName = item.titleDisplay;
    doc.source = item.sourceDisplay;
    doc.sourceDate = item.sourceDate ? new Date(item.sourceDate).toISOString().split('T')[0] : undefined;
    doc.citedDate = item.citedDate ? new Date(item.citedDate).toISOString().split('T')[0] : undefined;
    doc.note = item.citationNotes;
    doc.includedInIds = item.isSubmitted;
    doc.environment = this.getEnvironmentFromBaseUrl();
    doc.id = String(item.fileId ?? '');
    return doc;
  }

  /**
   * Helper: Check if ReferenceDetails matches the requested reference types
   */
  private matchesReferenceType(
    details: ReferenceDetails,
    referenceTypes?: { usPatent?: boolean; foreignPatent?: boolean; npl?: boolean }
  ): boolean {
    if (!referenceTypes || Object.keys(referenceTypes).length === 0) {
      return false;
    }
    const typeNum = Number(details.referenceType);
    if (referenceTypes.usPatent && typeNum === 3) return true;
    if (referenceTypes.foreignPatent && typeNum === 2) return true;
    if (referenceTypes.npl && typeNum === 1) return true;
    return false;
  }

  /**
   * Helper: Flatten a case's trifold documents across requested sections.
   * Returns array of CaseDownloadDocument ready for Excel.
   */
  private flattenCaseTrifoldDocumentsFromTrifold(trifold: CommonIPTrifold, trifolds: IP.TrifoldEnum[]): CaseDownloadDocument[] {
    return trifolds.flatMap((trifoldLetter) => {
      const wsFiles = trifold[trifoldLetter] || [];
      return wsFiles.map((wsFile) => {
        const doc = new CaseDownloadDocument();
        doc.case = trifold.identifier;
        doc.trifold = trifoldLetter;
        doc.type = wsFile.docSubtype;
        doc.title = wsFile.title;
        doc.date = wsFile.documentDate ? new Date(wsFile.documentDate).toLocaleDateString('en-US') : undefined;
        doc.documentName = wsFile.fileName;
        doc.id = String(wsFile.fileId ?? '');
        doc.status = wsFile.status;
        doc.environment = this.getEnvironmentFromBaseUrl();
        return doc;
      });
    });
  }

  private getEnvironmentFromBaseUrl(): string {
    const baseUrl = this.settingsService.getParsedBaseUrl();
    if (!baseUrl) return '';

    const parts = baseUrl.split('/').filter((p) => p.length > 0);
    return parts[parts.length - 1] || '';
  }

  /**
   * Returns the family case identifier strings for a given case, based on IP type.
   * Returns an empty array on error so the overall flow is never interrupted.
   */
  private getFamilyIdentifiers(identifier: string, ipType: IP.TrifTypeEnum): Observable<string[]> {
    return this.apiService.findFamilyAssociations(identifier, ipType).pipe(catchError(() => of([])));
  }

  /**
   * Given a list of case identifiers, fetches all family members for each and
   * returns grouped data: each original with its unique family members.
   * Family members that are already in the original list, or already claimed by
   * a previous group, are excluded to avoid duplicates.
   */
  expandIdentifiersWithFamilyGrouped(identifiers: string[], ipType: IP.TrifTypeEnum): Observable<{ original: string; family: string[] }[]> {
    if (identifiers.length === 0) return of([]);

    return forkJoin(
      identifiers.map((id) => this.getFamilyIdentifiers(id, ipType).pipe(map((familyIds) => ({ original: id, allFamily: familyIds }))))
    ).pipe(
      map((groups) => {
        const seen = new Set(identifiers);
        return groups.map((g) => {
          const family = g.allFamily.filter((id) => {
            if (seen.has(id)) return false;
            seen.add(id);
            return true;
          });
          return { original: g.original, family };
        });
      })
    );
  }
}
